import { type RefObject, useEffect, useRef } from "react";

export interface UseFloatingScrollbarOptions {
  hideDelayMs?: number;
  minThumbHeight?: number;
  trackInsetTopRef?: RefObject<HTMLElement | null>;
}

export interface UseFloatingScrollbarResult {
  scrollContainerRef: RefObject<HTMLDivElement | null>;
  thumbRef: RefObject<HTMLDivElement | null>;
  handleScroll: () => void;
}

/**
 * Provides refs and a scroll handler for a custom overlay scrollbar that floats
 * over the scroll container's content. The thumb fades in on scroll and fades
 * out after `hideDelayMs` of inactivity. All updates happen via direct DOM
 * mutation, so scrolling does not trigger React re-renders.
 *
 * Attach `scrollContainerRef` to the scrollable element and pass `handleScroll`
 * to its `onScroll` prop. Attach `thumbRef` to an absolutely-positioned thumb
 * element layered over the scroll container.
 */
export const useFloatingScrollbar = (options?: UseFloatingScrollbarOptions): UseFloatingScrollbarResult => {
  const hideDelayMs = options?.hideDelayMs ?? 800;
  const minThumbHeight = options?.minThumbHeight ?? 30;

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLDivElement>(null);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleScroll = () => {
    const el = scrollContainerRef.current;
    const thumb = thumbRef.current;
    if (!el || !thumb) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    if (scrollHeight <= clientHeight) {
      thumb.style.opacity = "0";
      return;
    }
    const inset = options?.trackInsetTopRef?.current?.offsetHeight ?? 0;
    const height = Math.max((clientHeight / scrollHeight) * clientHeight, minThumbHeight);
    const availableTrack = Math.max(clientHeight - inset - height, 0);
    const top = inset + (scrollTop / (scrollHeight - clientHeight)) * availableTrack;
    thumb.style.height = `${height}px`;
    thumb.style.transform = `translateY(${top}px)`;
    thumb.style.opacity = "1";
    if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    hideTimeoutRef.current = setTimeout(() => {
      if (thumbRef.current) thumbRef.current.style.opacity = "0";
    }, hideDelayMs);
  };

  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    };
  }, []);

  return { scrollContainerRef, thumbRef, handleScroll };
};
